/**
 * Web-side wrapper around the `fetchBacklogComments` callable. Factored
 * as a `_runner` module so vitest can swap in a mock without going
 * through `firebase/functions` — mirrors the pattern in `backlogSearch.ts`.
 */
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirebaseApp } from './firebase';

export interface FetchBacklogCommentsBody {
  projectId: string;
  issueKey: string;
  maxId?: number;
  count?: number;
}

export interface FetchedBacklogComment {
  commentId: number;
  authorName: string;
  body: string;
  /** ISO-8601 timestamp from Backlog's `created` field. */
  createdAt: string;
}

export type FetchBacklogCommentsResult =
  | {
      ok: true;
      comments: FetchedBacklogComment[];
      hasMore: boolean;
    }
  | {
      ok: false;
      reason: 'config-missing' | 'secret-error' | 'backlog-error';
      message: string;
    };

export type FetchBacklogCommentsRunner = (
  body: FetchBacklogCommentsBody,
) => Promise<FetchBacklogCommentsResult>;

let _runner: FetchBacklogCommentsRunner | null = null;

/** Test seam — swap in a mock runner. Returns the previous runner. */
export function setFetchBacklogCommentsRunnerForTests(
  runner: FetchBacklogCommentsRunner | null,
): FetchBacklogCommentsRunner | null {
  const prev = _runner;
  _runner = runner;
  return prev;
}

function defaultRunner(): FetchBacklogCommentsRunner {
  return async (body) => {
    const fns = getFunctions(getFirebaseApp(), 'asia-northeast1');
    const callable = httpsCallable<FetchBacklogCommentsBody, FetchBacklogCommentsResult>(
      fns,
      'fetchBacklogComments',
    );
    const res = await callable(body);
    return res.data;
  };
}

export async function fetchBacklogComments(
  body: FetchBacklogCommentsBody,
): Promise<FetchBacklogCommentsResult> {
  const run = _runner ?? defaultRunner();
  return await run(body);
}
